import { defineComponent as _defineComponent } from 'vue'
import NavigationBar from "../shared/NavigationBar.vue";
import {
  FilterKeyValueType,
  IFilter,
  IOption,
  FilterRelationMap,
  FilterGroupCode,
} from "@/domain/entities/filter/filter.types";
import {
  HttpBaseFilterWithRelation,
  OptionMapper,
} from "@/repositories/lineInfo/lineInfoRepository.types";
import { PropType, computed, inject, onMounted, ref } from "vue";
import {
  BookRequiredType,
  PROVIDE_KEY_SHOW_TOAST_MESSAGE,
  SERVICE_UID,
  ShowToastMessage,
} from "@/types";
import CustomSelect from "../shared/selects/CustomSelect.vue";
import TargetCalendarInput from "../shared/inputs/TargetCalendarInput.vue";
import FooterButton from "../shared/button/FooterButton.vue";
import LineSearchFilterItems from "./LineSearchFilterItems.vue";
import { useServiceStore } from "@/stores/serviceStore";
import { useLineStore } from "@/stores/lineStore";
import dayjs from "dayjs";
import ScrollTopControl from "@/components/shared/ScrollTopControl.vue";
import TimeSlider2 from "../shared/TimeSlider2.vue";


const __default__ = {
  name: "LineSearchFilter",
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
  bookRequiredType: {
    type: String as PropType<BookRequiredType>,
    required: true,
  },
  appliedSearchParamsMap: {
    type: Object as PropType<Map<string, IOption | IOption[] | null>>,
    required: true,
  },
},
  emits: ["onClickClose", "onDeletedFilter", "onClickApplyBtn"],
  setup(__props, { expose: __expose, emit: __emit }) {

const props = __props;

const emit = __emit;

const showToastMessage = inject(
  PROVIDE_KEY_SHOW_TOAST_MESSAGE
) as ShowToastMessage;

const lineStore = useLineStore();
const serviceStore = useServiceStore();
const isPublicLineApi = computed(
  () => !!serviceStore.serviceInfo?.isPublicLineApi
);

const scrollContainer = ref<HTMLElement>();

const emitOnClickClose = () => {
  // X 버튼 클릭시 적용된 상태로 복원
  restoreAppliedFilters();
  emit("onClickClose");
};
const emitOnClickApplyBtn = () => {
  emit("onClickApplyBtn", searchParamsMap.value, apiParams.value);
};

// 적용된 필터 상태로 모든 필터들을 복원
const restoreAppliedFilters = () => {
  searchParamsMap.value = new Map(props.appliedSearchParamsMap);

  // 각 필터의 selectedOptions를 복원
  filters.value.forEach((filterRef) => {
    const filter = filterRef.value;
    if (filter?.key) {
      const savedValue = props.appliedSearchParamsMap?.get(filter.key) ?? null;
      filter.selectedOptions = savedValue || undefined;
    }
  });

  // searchParams도 복원
  rebuildSearchParamsFromMap();

  // visibleFilters 복원
  rebuildVisibleFilters();
};

const rebuildVisibleFilters = () => {
  // 1. 기본 필터만 표시
  visibleFilters.value = new Set(baseFilterKeys);

  // 2. 모든 부모 필터 순회
  Object.entries(filterGroupKeyMap).forEach(
    ([_parentGroupCode, parentFilterKey]) => {
      const parentFilter = filters.value.find(
        (f) => f.value?.key === parentFilterKey
      )?.value;
      if (!parentFilter) return;

      // 3. 부모 필터의 선택값 확인
      const selectedOption = Array.isArray(parentFilter.selectedOptions)
        ? parentFilter.selectedOptions[0]
        : parentFilter.selectedOptions;

      // 4. "전체"가 아니면 자식 필터 표시
      if (selectedOption && selectedOption.key !== "") {
        const parentUid = selectedOption.key;
        const parentData = allFilterData.value.get(parentUid);
        if (parentData && parentData.childCustomCodes) {
          parentData.childCustomCodes.forEach((child) => {
            const childFilterKey =
              filterGroupKeyMap[
                child.childCodeGroup as keyof typeof filterGroupKeyMap
              ];
            if (childFilterKey && childFilterKey !== parentFilterKey) {
              visibleFilters.value.add(childFilterKey);
            }
          });
        }
      }
      // "전체"가 선택된 경우는 기본 필터만 유지 (아무것도 하지 않음)
    }
  );
};

// Map에서 searchParams 재구성
const rebuildSearchParamsFromMap = () => {
  const newSearchParams: Record<string, string | string[]> = {};

  searchParamsMap.value.forEach((value, key) => {
    if (key && value) {
      const filter = filters.value.find((f) => f.value?.key === key)?.value;
      if (filter) {
        newSearchParams[key] = getSelectedOptionsStr(filter, value);

        let selectedUid: string | undefined;
        const selectedOptions = Array.isArray(filter.selectedOptions)
          ? filter.selectedOptions[0]
          : filter.selectedOptions;

        selectedUid =
          selectedOptions?.key === "" ? undefined : selectedOptions?.key;

        if (filter.key && selectedUid) {
          // 자식 필터들 availableOptions 업데이트
          updateChildFiltersForParent(selectedUid, filter.key);
        }
      }
    }
  });

  searchParams.value = newSearchParams;
};

// 새로운 관계형 필터 매퍼 (HttpBaseFilterWithRelation 타입으로 캐스팅)
const relationFilterMapper: OptionMapper<HttpBaseFilterWithRelation> = (
  data
) => {
  const relationData = data as HttpBaseFilterWithRelation;
  return {
    key: relationData.uid,
    name: relationData.codeName,
    optionMetadata: relationData, // 전체 응답 데이터를 메타데이터로 저장
  };
};

// 필터 관계 관리 상태
const filterRelationMap = ref<FilterRelationMap>({});
const allFilterData = ref<Map<string, HttpBaseFilterWithRelation>>(new Map());

// 무한루프 방지 플래그
const isUpdatingFilters = ref(false);

// 기본 필터들 (항상 표시)
const baseFilterKeys = [
  "campusCodes", // 캠퍼스
  "driveDate", // 날짜
  "beginStationTimeFrom", // 시간 (시작)
  "beginStationTimeTo", // 시간 (종료)
];
const visibleFilters = ref<Set<string>>(new Set(baseFilterKeys));

// 필터 그룹 코드와 키 매핑
const filterGroupKeyMap = {
  [FilterGroupCode.CAMPUS]: "campusCodes",
  [FilterGroupCode.LINE_TIME]: "lineTimeCodes",
  [FilterGroupCode.LINE_DIRECTION]: "lineDirectionCodes",
  [FilterGroupCode.LINE_DISTRICT]: "lineDistrictCodes",
} as const;

const getFilter = async (
  pathParam: string,
  key: string,
  multiple = false,
  label?: string,
  dummyOptionName?: string
) => {
  const result = await lineStore.getFilter<HttpBaseFilterWithRelation>(
    pathParam,
    key,
    FilterKeyValueType.FilterKey_OptionKey,
    relationFilterMapper,
    isPublicLineApi.value,
    multiple,
    label,
    dummyOptionName
  );
  if (!result.isSuccess) {
    showToastMessage(`${label} 필터 목록을 가져오지 못했습니다`);
    return;
  }
  return result.data;
};

// 전체날짜가 선택된 경우 달력에서 날짜 선택 못하도록 disabled 처리
const isCalendarDisabled = computed(() => {
  const selectedOptions = dateFilter.value.selectedOptions;
  if (!selectedOptions) return true;
  if (Array.isArray(selectedOptions)) {
    return selectedOptions.some((option) => option.key !== "selectDate");
  } else {
    return selectedOptions.key !== "selectDate";
  }
});

const campusFilter = ref<IFilter>();
const lineTypeFilter = ref<IFilter>();
const lineDirectionFilter = ref<IFilter>();
const locationFilter = ref<IFilter>();

const dateOptions: IOption[] = [
  {
    key: "today",
    name: "오늘",
    value: dayjs().format("YYYY-MM-DD"),
    default: true,
  },
  {
    key: "all",
    name: "전체날짜",
    value: "",
  },
  {
    key: "selectDate",
    name: "날짜선택",
    value: "",
  },
];
const dateFilter = ref<IFilter>({
  key: "driveDate",
  type: FilterKeyValueType.FilterKey_OptionValue,
  options: dateOptions,
  availableOptions: dateOptions,
  selectedOptions: dateOptions.find((option) => option.default),
});

const bookOptions: IOption[] = [
  {
    key: "",
    name: "전체",
    default: true,
  },
  {
    key: "true",
    name: "예약",
  },
  {
    key: "false",
    name: "비예약",
  },
];
const bookFilter = ref<IFilter>({
  key: "bookYn",
  type: FilterKeyValueType.FilterKey_OptionKey,
  options: bookOptions,
  availableOptions: bookOptions,
});

const dayOfWeekOptions: IOption[] = [
  {
    key: "",
    name: "전체",
    default: true,
  },
  {
    key: "WKD-MON",
    name: "월요일",
  },
  {
    key: "WKD-TUE",
    name: "화요일",
  },
  {
    key: "WKD-WED",
    name: "수요일",
  },
  {
    key: "WKD-THU",
    name: "목요일",
  },
  {
    key: "WKD-FRI",
    name: "금요일",
  },
  {
    key: "WKD-SAT",
    name: "토요일",
  },
  {
    key: "WKD-SUN",
    name: "일요일",
  },
];
const dayOfWeekFilter = ref<IFilter>({
  key: "weekdayCodes",
  type: FilterKeyValueType.FilterKey_OptionKey,
  multiple: true,
  options: dayOfWeekOptions,
  availableOptions: dayOfWeekOptions,
});

const fromOptions = [
  {
    key: "fromTime",
    name: "fromTime",
    value: "00:00",
    default: true,
  },
];
const fromTimeFilter = ref<IFilter>({
  key: "beginStationTimeFrom",
  type: FilterKeyValueType.FilterKey_OptionValue,
  options: fromOptions,
  availableOptions: fromOptions,
});

const toOptions = [
  {
    key: "toTime",
    name: "toTime",
    value: "24:00",
    default: true,
  },
];
const toTimeFilter = ref<IFilter>({
  key: "beginStationTimeTo",
  type: FilterKeyValueType.FilterKey_OptionValue,
  options: toOptions,
  availableOptions: toOptions,
});

const filters = ref([
  campusFilter,
  lineTypeFilter,
  lineDirectionFilter,
  locationFilter,
  dateFilter,
  bookFilter,
  dayOfWeekFilter,
  fromTimeFilter,
  toTimeFilter,
]);

const searchParamsMap = ref(new Map<string, IOption | IOption[] | null>());
const searchParams = ref<Record<string, string | string[]>>({});
const apiParams = computed(() => {
  const paramEntries: string[] = [];
  for (const [key, value] of Object.entries(searchParams.value)) {
    if (key && value) {
      paramEntries.push(`${key}=${value}`);
    }
  }

  return paramEntries.join("&");
});

const defaultApiParams = computed(
  () => `driveDate=${dayjs().format("YYYY-MM-DD")}`
);

const resetSelectedOptionAndSearchParams = (filter: IFilter) => {
  // 무한루프 방지: updateFilter 호출 대신 직접 값 설정
  if (
    filter.key === "beginStationTimeFrom" ||
    filter.key === "beginStationTimeTo"
  ) {
    filter.selectedOptions = undefined;
    if (filter.key) {
      delete searchParams.value[filter.key];
      searchParamsMap.value.delete(filter.key);
    }
  } else {
    const defaultOptions = filter.options.find((option) => option.default);
    filter.selectedOptions = defaultOptions ?? undefined;
    if (filter.key && defaultOptions) {
      searchParams.value[filter.key] = getSelectedOptionsStr(
        filter,
        defaultOptions
      );
      searchParamsMap.value.set(filter.key, defaultOptions);
      handleParentFilterChange(filter.key, defaultOptions);
    } else if (filter.key) {
      delete searchParams.value[filter.key];
      searchParamsMap.value.delete(filter.key);
      handleParentFilterChange(filter.key, null);
    }
  }
};

const deleteSearchParam = (key: string) => {
  const targetFilter = filters.value.find(
    (filter) => filter.value?.key === key
  )?.value;
  if (!targetFilter) return;

  resetSelectedOptionAndSearchParams(targetFilter);

  emit("onDeletedFilter", searchParamsMap.value, apiParams.value);
};

const resetFilter = () => {
  // 무한루프 방지
  if (isUpdatingFilters.value) return;

  isUpdatingFilters.value = true;
  try {
    filters.value.forEach((filter) => {
      if (filter.value) {
        resetSelectedOptionAndSearchParams(filter.value);
      }
    });

    // 캠퍼스 필터 초기화 시 관련 필터들도 초기화
    if (campusFilter.value) {
      handleParentFilterChange("campusCodes", null);
    }
  } finally {
    isUpdatingFilters.value = false;
  }
};

const getSelectedOptions = (
  filter: IFilter | undefined
): IOption | IOption[] | null => {
  if (!filter?.key) return null;
  return searchParamsMap.value.get(filter.key) || null;
};

const getSelectedOptionsStr = (
  filter: IFilter,
  selectedOptions: IOption | IOption[] | undefined
): string => {
  if (!selectedOptions) return "";

  const optionKey = getOptionKeyByFilterType(filter.type);
  if (Array.isArray(selectedOptions)) {
    return selectedOptions.map((option) => option[optionKey]).join(", ");
  } else {
    return selectedOptions[optionKey];
  }
};

const getOptionKeyByFilterType = (
  filterType: FilterKeyValueType
): keyof IOption => {
  switch (filterType) {
    case FilterKeyValueType.FilterKey_OptionKey:
      return "key";
    case FilterKeyValueType.FilterKey_OptionValue:
      return "value";
    case FilterKeyValueType.OptionKey_OptionValue:
      return "value";
    default:
      return "key";
  }
};

const isEmptySelectedOptionsKey = (
  selectedOptions: IOption | IOption[] | null
) => {
  if (Array.isArray(selectedOptions)) {
    return selectedOptions.some((option) => !option.key?.length);
  } else {
    return !selectedOptions?.key?.length;
  }
};

const updateFilter = (
  filter: IFilter,
  selectedOptions: IOption | IOption[] | null
) => {
  if (!filter.key) return;

  // 무한루프 방지
  if (isUpdatingFilters.value) return;

  // 재클릭 처리: 이미 선택된 옵션을 다시 클릭한 경우 "전체"로 변경
  if (
    selectedOptions &&
    !Array.isArray(selectedOptions) &&
    filter.selectedOptions &&
    !Array.isArray(filter.selectedOptions) &&
    filter.selectedOptions.key === selectedOptions.key &&
    selectedOptions.key !== ""
  ) {
    // "전체" 옵션 찾기
    const allOption = filter.options.find((option) => option.key === "");
    selectedOptions = allOption || null;
  }

  if (selectedOptions === null || isEmptySelectedOptionsKey(selectedOptions)) {
    filter.selectedOptions = undefined;
    delete searchParams.value[filter.key];
    searchParamsMap.value.delete(filter.key);
  } else {
    filter.selectedOptions = selectedOptions;
    searchParams.value[filter.key] = getSelectedOptionsStr(
      filter,
      selectedOptions
    );
    searchParamsMap.value.set(filter.key, selectedOptions);
  }

  // 부모 필터 변경시 관련 로직 수행 (무한루프 방지 플래그 설정)
  isUpdatingFilters.value = true;
  try {
    handleParentFilterChange(filter.key, selectedOptions);
  } finally {
    isUpdatingFilters.value = false;
  }
};

// 부모 필터 변경 처리 (범용)
const handleParentFilterChange = (
  filterKey: string,
  selectedOptions: IOption | IOption[] | null
) => {
  let selectedUid: string | undefined;

  if (selectedOptions && !Array.isArray(selectedOptions)) {
    selectedUid = selectedOptions.key === "" ? undefined : selectedOptions.key;
  }

  // 캠퍼스 필터는 마스터 필터로 특별 처리
  if (filterKey === "campusCodes") {
    if (!selectedUid) {
      // 캠퍼스가 "전체"로 선택된 경우 기본 필터들만 표시
      const newVisibleFilters = new Set(baseFilterKeys);
      visibleFilters.value = newVisibleFilters;

      // 모든 자식 필터들 초기화
      resetAllChildFilters();
    } else {
      // 캠퍼스가 선택된 경우 전체 필터 표시 업데이트
      updateVisibleFilters();
      // 캠퍼스의 자식 필터들 업데이트
      updateChildFiltersForParent(selectedUid, filterKey);
    }
  } else {
    // 다른 필터들은 캠퍼스가 선택되어 있을 때만 동작
    const campusFilter = filters.value.find(
      (f) => f.value?.key === "campusCodes"
    )?.value;
    const campusSelected =
      campusFilter?.selectedOptions &&
      !Array.isArray(campusFilter.selectedOptions) &&
      campusFilter.selectedOptions.key !== "";

    if (campusSelected) {
      // 캠퍼스가 선택되어 있을 때만 자식 관계 처리
      if (selectedUid) {
        // 해당 필터의 자식들을 표시하고 옵션 업데이트
        updateChildFiltersForParent(selectedUid, filterKey);
        // 전체 필터 표시 업데이트 (다른 선택된 필터들 고려)
        updateVisibleFilters();
      } else {
        // "전체"로 선택된 경우: 해당 필터만 "전체"로 변경, 다른 필터들은 유지
        // 필터 표시는 캠퍼스 기반으로 유지
        updateVisibleFilters();
      }
    }
    // 캠퍼스가 선택되지 않은 경우에는 아무것도 하지 않음 (기본 필터만 표시 상태 유지)
  }
};

// 모든 자식 필터들 초기화 (캠퍼스 "전체" 선택시)
const resetAllChildFilters = () => {
  Object.values(filterGroupKeyMap).forEach((filterKey) => {
    if (filterKey !== "campusCodes") {
      const filter = filters.value.find(
        (f) => f.value?.key === filterKey
      )?.value;
      if (filter) {
        // 모든 옵션을 다시 사용 가능하게 설정
        filter.availableOptions = [...filter.options];
        // 선택 초기화
        resetSelectedOptionAndSearchParams(filter);
      }
    }
  });
};

// 특정 부모 필터에 대한 자식 필터들 업데이트
const updateChildFiltersForParent = (
  parentUid: string,
  parentFilterKey: string
) => {
  const parentData = allFilterData.value.get(parentUid);
  if (!parentData || !parentData.childCustomCodes) return;

  // 부모의 자식 코드 그룹들 확인
  const childCodeGroups = new Set(
    parentData.childCustomCodes.map((child) => child.childCodeGroup)
  );

  // 자식 필터들을 표시하고 옵션 업데이트
  childCodeGroups.forEach((childCodeGroup) => {
    const childFilterKey = Object.entries(filterGroupKeyMap).find(
      ([code]) => code === childCodeGroup
    )?.[1];

    if (childFilterKey && childFilterKey !== parentFilterKey) {
      // 자식 필터 표시
      visibleFilters.value.add(childFilterKey);
      // 자식 필터 옵션 업데이트
      updateChildFilterOptions(parentUid, childFilterKey);
    }
  });
};

const updateDateFilter = (date: string, filter: IFilter) => {
  const selectedOptions = filter.selectedOptions;
  if (!filter.key || !selectedOptions) return;
  if (Array.isArray(selectedOptions)) return;

  const optionKey = getOptionKeyByFilterType(filter.type);
  selectedOptions[optionKey] = date;
  searchParams.value[filter.key] = getSelectedOptionsStr(
    filter,
    selectedOptions
  );
  searchParamsMap.value.set(filter.key, selectedOptions);
};

// 시간 필터 전용 업데이트 함수들
const updateFromTimeFilter = (
  filter: IFilter,
  selectedOptions: IOption | IOption[] | null
) => {
  if (!filter.key) return;

  // 무한루프 방지
  if (isUpdatingFilters.value) return;

  if (selectedOptions === null || isEmptySelectedOptionsKey(selectedOptions)) {
    filter.selectedOptions = undefined;
    delete searchParams.value[filter.key];
    searchParamsMap.value.delete(filter.key);
  } else {
    filter.selectedOptions = selectedOptions;
    searchParams.value[filter.key] = getSelectedOptionsStr(
      filter,
      selectedOptions
    );
    searchParamsMap.value.set(filter.key, selectedOptions);
  }
};

const updateToTimeFilter = (
  filter: IFilter,
  selectedOptions: IOption | IOption[] | null
) => {
  if (!filter.key) return;

  // 무한루프 방지
  if (isUpdatingFilters.value) return;

  if (selectedOptions === null || isEmptySelectedOptionsKey(selectedOptions)) {
    filter.selectedOptions = undefined;
    delete searchParams.value[filter.key];
    searchParamsMap.value.delete(filter.key);
  } else {
    filter.selectedOptions = selectedOptions;
    searchParams.value[filter.key] = getSelectedOptionsStr(
      filter,
      selectedOptions
    );
    searchParamsMap.value.set(filter.key, selectedOptions);
  }
};

const getFilters = async () => {
  const publicPath = isPublicLineApi.value ? "/public" : "";
  const path = `${publicPath}/service/${SERVICE_UID}/line/custom-code`;

  try {
    // 각 필터 그룹별로 개별 API 호출 (기존 방식 유지하되 응답 처리 방식 변경)
    const [campusResult, lineTypeResult, lineDirectionResult, locationResult] =
      await Promise.all([
        getFilter(
          `${path}?codeGroup=CPS`,
          "campusCodes",
          false,
          "캠퍼스",
          "전체"
        ),
        getFilter(
          `${path}?codeGroup=LTM`,
          "lineTimeCodes",
          false,
          "노선형태",
          "전체"
        ),
        getFilter(
          `${path}?codeGroup=LDR`,
          "lineDirectionCodes",
          false,
          "방향",
          "전체"
        ),
        getFilter(
          `${path}?codeGroup=LDT`,
          "lineDistrictCodes",
          false,
          "지역",
          "전체"
        ),
      ]);

    // 각 필터 결과 설정
    campusFilter.value = campusResult;
    lineTypeFilter.value = lineTypeResult;
    lineDirectionFilter.value = lineDirectionResult;
    locationFilter.value = locationResult;

    // 부모-자식 관계 맵 구성을 위해 모든 필터 데이터 수집
    const allFilterData: HttpBaseFilterWithRelation[] = [];

    // 각 필터에서 원본 응답 데이터 추출 (optionMetadata에 저장된 데이터)
    if (campusResult?.options) {
      campusResult.options.forEach((option) => {
        if (option.optionMetadata) {
          allFilterData.push(
            option.optionMetadata as HttpBaseFilterWithRelation
          );
        }
      });
    }

    if (lineTypeResult?.options) {
      lineTypeResult.options.forEach((option) => {
        if (option.optionMetadata) {
          allFilterData.push(
            option.optionMetadata as HttpBaseFilterWithRelation
          );
        }
      });
    }

    if (lineDirectionResult?.options) {
      lineDirectionResult.options.forEach((option) => {
        if (option.optionMetadata) {
          allFilterData.push(
            option.optionMetadata as HttpBaseFilterWithRelation
          );
        }
      });
    }

    if (locationResult?.options) {
      locationResult.options.forEach((option) => {
        if (option.optionMetadata) {
          allFilterData.push(
            option.optionMetadata as HttpBaseFilterWithRelation
          );
        }
      });
    }

    // 관계 맵 생성
    buildFilterRelationMap(allFilterData);
  } catch (error) {
    console.error("필터 로딩 중 오류:", error);
    showToastMessage("필터 목록을 가져오지 못했습니다");
  }
};

const handleClickDateFilter = (
  dateFilter: IFilter,
  selectedOptions: IOption | IOption[] | null
) => {
  updateFilter(dateFilter, selectedOptions);

  const selectedOption = Array.isArray(dateFilter.selectedOptions)
    ? dateFilter.selectedOptions?.[0]
    : dateFilter.selectedOptions;
  if (selectedOption?.key !== "today") return;

  const today = dayjs().format("YYYY-MM-DD");
  updateDateFilter(today, dateFilter);
};

// 필터 관계 맵 생성
const buildFilterRelationMap = (filterData: HttpBaseFilterWithRelation[]) => {
  const relationMap: FilterRelationMap = {};

  filterData.forEach((item) => {
    // 전체 필터 데이터 저장
    allFilterData.value.set(item.uid, item);

    // 자식 관계 매핑
    if (item.childCustomCodes?.length) {
      relationMap[item.uid] = {};

      item.childCustomCodes.forEach((child) => {
        if (!relationMap[item.uid][child.childCodeGroup]) {
          relationMap[item.uid][child.childCodeGroup] = [];
        }
        relationMap[item.uid][child.childCodeGroup].push(child);
      });
    }
  });

  filterRelationMap.value = relationMap;
};

// 동적 필터 표시 업데이트 (범용)
const updateVisibleFilters = (selectedUid?: string) => {
  // 기본 필터는 항상 표시
  const newVisibleFilters = new Set(baseFilterKeys);

  // 캠퍼스 필터 선택 상태 확인
  const campusFilter = filters.value.find(
    (f) => f.value?.key === "campusCodes"
  )?.value;
  const campusSelected =
    campusFilter?.selectedOptions &&
    !Array.isArray(campusFilter.selectedOptions) &&
    campusFilter.selectedOptions.key !== "";

  // 캠퍼스가 선택되지 않은 경우 기본 필터들만 표시
  if (!campusSelected) {
    visibleFilters.value = newVisibleFilters;
    return;
  }

  // 캠퍼스가 선택된 경우, 최소한 캠퍼스의 직접 자식 필터들은 표시
  const campusUid = (campusFilter.selectedOptions as IOption).key;
  const campusChildFilters = filterRelationMap.value[campusUid];
  if (campusChildFilters) {
    Object.keys(campusChildFilters).forEach((childCodeGroup) => {
      const filterKey = Object.entries(filterGroupKeyMap).find(
        ([code]) => code === childCodeGroup
      )?.[1];
      if (filterKey) {
        newVisibleFilters.add(filterKey);
      }
    });
  }

  // 추가로 현재 선택된 다른 부모 필터들의 자식 필터들도 표시
  const selectedParents = new Set<string>();

  // 모든 필터들의 선택사항 확인 (캠퍼스 포함)
  filters.value.forEach((filterRef) => {
    const filter = filterRef.value;
    if (
      filter?.selectedOptions &&
      !Array.isArray(filter.selectedOptions) &&
      filter.selectedOptions.key !== ""
    ) {
      selectedParents.add(filter.selectedOptions.key);
    }
  });

  // 선택된 모든 부모들의 자식 필터들 추가 표시
  selectedParents.forEach((parentUid) => {
    const childFilters = filterRelationMap.value[parentUid];
    if (childFilters) {
      Object.keys(childFilters).forEach((childCodeGroup) => {
        const filterKey = Object.entries(filterGroupKeyMap).find(
          ([code]) => code === childCodeGroup
        )?.[1];
        if (filterKey) {
          newVisibleFilters.add(filterKey);
        }
      });
    }
  });

  visibleFilters.value = newVisibleFilters;

  // 자식 필터가 화면에서 사라질 때 필터 상태 정리
  filters.value.forEach((filterRef) => {
    const filter = filterRef.value;
    if (!filter?.key) return;
    if (visibleFilters.value.has(filter.key)) return;

    // searchParamsMap, searchParams.value에서 삭제
    searchParamsMap.value.delete(filter.key);
    delete searchParams.value[filter.key];
    // default: true 옵션이 있으면 그걸로, 없으면 undefined
    const defaultOption = filter.options.find((opt) => opt.default);
    filter.selectedOptions = defaultOption ?? undefined;
  });
};

// 선택된 부모에 따른 자식 옵션 필터링
const updateChildFilterOptions = (
  parentUid: string,
  childFilterKey: string
) => {
  const childFilter = filters.value.find(
    (f) => f.value?.key === childFilterKey
  )?.value;
  if (!childFilter) return;

  const parentData = allFilterData.value.get(parentUid);
  if (!parentData) return;

  // 부모의 자식 코드 그룹 찾기
  const childCodeGroup = Object.entries(filterGroupKeyMap).find(
    ([_, key]) => key === childFilterKey
  )?.[0];

  if (!childCodeGroup) return;

  const childRelations = filterRelationMap.value[parentUid]?.[childCodeGroup];
  if (!childRelations) return;

  // 관련된 자식 옵션들만 필터링
  const availableChildOptions = childFilter.options.filter((option) => {
    if (option.key === "") return true; // "전체" 옵션은 항상 포함
    return childRelations.some((child) => child.childUid === option.key);
  });

  childFilter.availableOptions = availableChildOptions;

  // 현재 선택된 옵션이 유효하지 않으면 초기화
  if (
    childFilter.selectedOptions &&
    !isValidSelectedOption(childFilter.selectedOptions, availableChildOptions)
  ) {
    resetSelectedOptionAndSearchParams(childFilter);
  }
};

// 선택된 옵션이 유효한지 확인
const isValidSelectedOption = (
  selectedOptions: IOption | IOption[],
  availableOptions: IOption[]
): boolean => {
  if (Array.isArray(selectedOptions)) {
    return selectedOptions.every((selected) =>
      availableOptions.some((available) => available.key === selected.key)
    );
  } else {
    return availableOptions.some(
      (available) => available.key === selectedOptions.key
    );
  }
};

// 모든 필터의 기본값 초기화
const initializeFilterDefaults = (filter: IFilter) => {
  const defaultOption =
    filter.selectedOptions || filter.options.find((option) => option.default);
  if (defaultOption && filter.key) {
    filter.selectedOptions = defaultOption;
    if (!Array.isArray(defaultOption)) {
      searchParams.value[filter.key] = getSelectedOptionsStr(
        filter,
        defaultOption
      );
      searchParamsMap.value.set(filter.key, defaultOption);
    }
  }
};

onMounted(async () => {
  // dateFilter 기본값 초기화
  initializeFilterDefaults(dateFilter.value);

  // bookFilter 기본값 초기화 (전체 옵션)
  if (bookFilter.value) {
    initializeFilterDefaults(bookFilter.value);
  }

  await getFilters();

  // 초기 캠퍼스 필터 설정 (기본적으로 캠퍼스 필터만 표시)
  updateVisibleFilters();
});

__expose({ deleteSearchParam });

const __returned__ = { props, emit, showToastMessage, lineStore, serviceStore, isPublicLineApi, scrollContainer, emitOnClickClose, emitOnClickApplyBtn, restoreAppliedFilters, rebuildVisibleFilters, rebuildSearchParamsFromMap, relationFilterMapper, filterRelationMap, allFilterData, isUpdatingFilters, baseFilterKeys, visibleFilters, filterGroupKeyMap, getFilter, isCalendarDisabled, campusFilter, lineTypeFilter, lineDirectionFilter, locationFilter, dateOptions, dateFilter, bookOptions, bookFilter, dayOfWeekOptions, dayOfWeekFilter, fromOptions, fromTimeFilter, toOptions, toTimeFilter, filters, searchParamsMap, searchParams, apiParams, defaultApiParams, resetSelectedOptionAndSearchParams, deleteSearchParam, resetFilter, getSelectedOptions, getSelectedOptionsStr, getOptionKeyByFilterType, isEmptySelectedOptionsKey, updateFilter, handleParentFilterChange, resetAllChildFilters, updateChildFiltersForParent, updateDateFilter, updateFromTimeFilter, updateToTimeFilter, getFilters, handleClickDateFilter, buildFilterRelationMap, updateVisibleFilters, updateChildFilterOptions, isValidSelectedOption, initializeFilterDefaults, NavigationBar, CustomSelect, TargetCalendarInput, FooterButton, LineSearchFilterItems, ScrollTopControl, TimeSlider2 }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})